import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Image, CalendarDays, DollarSign, Mail, LogOut, Settings, CreditCard, FolderOpen, PenLine, Music, Users } from "lucide-react";
import { useAdminAuth, useAdminData, formatApiError } from "@/hooks/useAdminData";
import GalleryManager from "@/components/admin/GalleryManager";
import EventsManager from "@/components/admin/EventsManager";
import SiteSettings from "@/components/admin/SiteSettings";
import DonationsManager from "@/components/admin/DonationsManager";
import FileManager from "@/components/admin/FileManager";
import ContentManager from "@/components/admin/ContentManager";
import MediaCollectionManager from "@/components/admin/MediaCollectionManager";
import EventPosterManager from "@/components/admin/EventPosterManager";

export default function AdminPage() {
  const { user, checking, login, logout } = useAdminAuth();
  const { gallery, events, donations, messages, files, collections, siteSettings, loadData } = useAdminData(user);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError("");
    try {
      await login(loginForm.email, loginForm.password);
    } catch (err) {
      setLoginError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-[#4A4A4A]">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#FAFAFA]" data-testid="admin-login-page">
        <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm w-full max-w-md mx-4">
          <h2 className="text-3xl font-bold text-[#1A1A1A] text-center mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Admin Login
          </h2>
          {loginError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4 text-sm" data-testid="login-error">
              {loginError}
            </div>
          )}
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="admin-email" className="text-sm">Email</Label>
              <Input
                id="admin-email" type="email" value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                required className="rounded-xl mt-1" data-testid="admin-email-input"
              />
            </div>
            <div>
              <Label htmlFor="admin-password" className="text-sm">Password</Label>
              <Input
                id="admin-password" type="password" value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                required className="rounded-xl mt-1" data-testid="admin-password-input"
              />
            </div>
            <Button type="submit" className="btn-primary w-full" disabled={loginLoading} data-testid="admin-login-btn">
              {loginLoading ? "Logging in..." : "Login"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] bg-[#FAFAFA]" data-testid="admin-dashboard">
      <div className="section-container py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Admin Dashboard</h1>
            <p className="text-[#4A4A4A] text-sm">Welcome, {user.name || user.email}</p>
          </div>
          <Button onClick={logout} variant="outline" className="rounded-xl" data-testid="admin-logout-btn">
            <LogOut size={16} className="mr-2" /> Logout
          </Button>
        </div>

        <Tabs defaultValue="gallery" data-testid="admin-tabs">
          <div className="admin-tabs-scroll">
          <TabsList className="flex w-max gap-1 mb-6 bg-white border border-gray-100 rounded-xl p-1 h-auto">
            <TabsTrigger value="gallery" className="flex items-center gap-1.5 py-2.5 px-3 rounded-lg text-xs whitespace-nowrap data-[state=active]:bg-[#E8922F] data-[state=active]:text-black" data-testid="admin-tab-gallery">
              <Image size={14} /> Gallery
            </TabsTrigger>
            <TabsTrigger value="events" className="flex items-center gap-1.5 py-2.5 px-3 rounded-lg text-xs whitespace-nowrap data-[state=active]:bg-[#E8922F] data-[state=active]:text-black" data-testid="admin-tab-events">
              <CalendarDays size={14} /> Events
            </TabsTrigger>
            <TabsTrigger value="files" className="flex items-center gap-1.5 py-2.5 px-3 rounded-lg text-xs whitespace-nowrap data-[state=active]:bg-[#E8922F] data-[state=active]:text-black" data-testid="admin-tab-files">
              <FolderOpen size={14} /> Files
            </TabsTrigger>
            <TabsTrigger value="rehearsals" className="flex items-center gap-1.5 py-2.5 px-3 rounded-lg text-xs whitespace-nowrap data-[state=active]:bg-[#E8922F] data-[state=active]:text-black" data-testid="admin-tab-rehearsals">
              <Music size={14} /> Rehearsals
            </TabsTrigger>
            <TabsTrigger value="community" className="flex items-center gap-1.5 py-2.5 px-3 rounded-lg text-xs whitespace-nowrap data-[state=active]:bg-[#E8922F] data-[state=active]:text-black" data-testid="admin-tab-community">
              <Users size={14} /> Community
            </TabsTrigger>
            <TabsTrigger value="content" className="flex items-center gap-1.5 py-2.5 px-3 rounded-lg text-xs whitespace-nowrap data-[state=active]:bg-[#E8922F] data-[state=active]:text-black" data-testid="admin-tab-content">
              <PenLine size={14} /> Content
            </TabsTrigger>
            <TabsTrigger value="donate-setup" className="flex items-center gap-1.5 py-2.5 px-3 rounded-lg text-xs whitespace-nowrap data-[state=active]:bg-[#E8922F] data-[state=active]:text-black" data-testid="admin-tab-donate-setup">
              <CreditCard size={14} /> Donate
            </TabsTrigger>
            <TabsTrigger value="donations" className="flex items-center gap-1.5 py-2.5 px-3 rounded-lg text-xs whitespace-nowrap data-[state=active]:bg-[#E8922F] data-[state=active]:text-black" data-testid="admin-tab-donations">
              <DollarSign size={14} /> History
            </TabsTrigger>
            <TabsTrigger value="messages" className="flex items-center gap-1.5 py-2.5 px-3 rounded-lg text-xs whitespace-nowrap data-[state=active]:bg-[#E8922F] data-[state=active]:text-black" data-testid="admin-tab-messages">
              <Mail size={14} /> Messages
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-1.5 py-2.5 px-3 rounded-lg text-xs whitespace-nowrap data-[state=active]:bg-[#E8922F] data-[state=active]:text-black" data-testid="admin-tab-settings">
              <Settings size={14} /> Site
            </TabsTrigger>
          </TabsList>
          </div>

          <TabsContent value="gallery">
            <GalleryManager gallery={gallery} loadData={loadData} />
          </TabsContent>

          <TabsContent value="events">
            <EventsManager events={events} loadData={loadData} />
          </TabsContent>

          <TabsContent value="files">
            <FileManager files={files} loadData={loadData} />
          </TabsContent>

          <TabsContent value="rehearsals">
            <MediaCollectionManager collectionType="rehearsals" title="Choir Rehearsals" items={collections} loadData={loadData} />
          </TabsContent>

          <TabsContent value="community">
            <MediaCollectionManager collectionType="community" title="Community Events" items={collections} loadData={loadData} />
          </TabsContent>

          <TabsContent value="content">
            <h3 className="text-lg font-bold text-[#1A1A1A] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Edit Website Content</h3>
            <ContentManager siteSettings={siteSettings} loadData={loadData} />
          </TabsContent>

          <TabsContent value="donate-setup">
            <h3 className="text-lg font-bold text-[#1A1A1A] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Manage Donation Page</h3>
            <DonationsManager siteSettings={siteSettings} loadData={loadData} />
          </TabsContent>

          <TabsContent value="donations">
            <h3 className="text-lg font-bold text-[#1A1A1A] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Donation History ({donations.length})</h3>
            <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Package</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {donations.map(d => (
                    <TableRow key={d.id}>
                      <TableCell className="font-medium">{d.package_name}</TableCell>
                      <TableCell>${d.amount} {d.currency?.toUpperCase()}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${d.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {d.payment_status}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">{new Date(d.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {donations.length === 0 && <p className="text-center text-gray-400 py-8 text-sm">No donations yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="messages">
            <h3 className="text-lg font-bold text-[#1A1A1A] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Messages ({messages.length})</h3>
            <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Message</TableHead><TableHead>Date</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {messages.map(m => (
                    <TableRow key={m.id}>
                      <TableCell className="font-medium">{m.name}</TableCell>
                      <TableCell>{m.email}</TableCell>
                      <TableCell className="text-sm text-gray-500 max-w-[300px] truncate">{m.message}</TableCell>
                      <TableCell className="text-sm text-gray-500">{new Date(m.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {messages.length === 0 && <p className="text-center text-gray-400 py-8 text-sm">No messages yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="settings">
            <div className="space-y-8">
              <EventPosterManager siteSettings={siteSettings} loadData={loadData} />
              <SiteSettings siteSettings={siteSettings} loadData={loadData} />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
